import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories, siteInfo } from '@/mocks/comics';

function HomeSEO() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteInfo.name,
    url: siteInfo.url,
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteInfo.url}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function Home() {
  const hotComics = comics.filter((c) => c.isHot).slice(0, 8);
  const latestComics = comics.filter((c) => c.isNew).slice(0, 8);
  const rankedComics = [...comics].sort((a, b) => b.rating - a.rating).slice(0, 10);
  const [activeTab, setActiveTab] = useState<'hot' | 'new' | 'all'>('hot');

  const displayComics = activeTab === 'hot'
    ? hotComics
    : activeTab === 'new'
    ? latestComics
    : comics.slice(0, 12);

  return (
    <>
      <ScrollToTop />
      <HomeSEO />
      <Navbar />

      <main>
        <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20Korean%20manhwa%20illustration%20background%20with%20soft%20pastel%20gradients%20and%20ink%20wash%20texture%2C%20dreamy%20atmosphere%20with%20floating%20petals%2C%20warm%20peach%20and%20cream%20tones%2C%20subtle%20comic%20panel%20lines%2C%20elegant%20minimal%20composition%2C%20clean%20and%20airy&width=1800&height=900&seq=home-hero-bg&orientation=landscape"
            alt="韩漫漫画网 - 免费高清韩国漫画在线阅读"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/15 to-black/45"></div>
          <div className="relative z-10 w-full max-w-[900px] mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-5xl font-bold font-heading text-white mb-4 leading-tight">
              高清韩国漫画
              <span className="block text-xl md:text-2xl font-normal mt-2 opacity-90">
                无遮挡下拉式阅读，畅享流畅浏览体验
              </span>
            </h1>
            <p className="text-white/75 text-sm md:text-base max-w-[600px] mx-auto leading-relaxed">
              韩漫漫画网提供免费漫画大全，界面简洁，加载迅速。海量高清韩国漫画每日更新，让您随时随地畅享阅读乐趣。
            </p>
            <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
              <Link
                to={`/category/${categories[0].slug}`}
                className="px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-white/15 text-white border border-white/25 rounded-full text-sm font-semibold hover:bg-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-[1440px] mx-auto px-4 md:px-6 -mt-10 relative z-20">
          <div className="bg-background-50 rounded-2xl p-5 md:p-8">
            <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-8 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-primary-50 transition-colors group cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-primary-50 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-xl md:text-2xl text-primary-500`}></i>
                  </div>
                  <span className="text-xs md:text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                精选漫画
              </h2>
              <p className="text-sm text-foreground-500 mt-1">每日更新，精彩不容错过</p>
            </div>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {(['hot', 'new', 'all'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === tab
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {tab === 'hot' ? '热门推荐' : tab === 'new' ? '最新上架' : '全部漫画'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
            {displayComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to={`/category/${categories[0].slug}`}
              className="inline-flex items-center gap-2 px-8 py-3 border border-foreground-300 rounded-full text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-500 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              查看全部漫画
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        <section className="bg-background-100/70 py-12 md:py-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                    人气排行榜
                  </h2>
                  <span className="text-xs text-foreground-500 bg-background-200/70 px-3 py-1 rounded-full">
                    实时更新
                  </span>
                </div>
                <div className="space-y-1">
                  {rankedComics.map((comic, index) => (
                    <ComicCard
                      key={comic.id}
                      comic={comic}
                      variant="ranking"
                      rank={index + 1}
                    />
                  ))}
                </div>
              </div>

              <div className="lg:w-[380px]">
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">
                  最新更新
                </h2>
                <div className="space-y-4">
                  {comics.slice(0, 6).map((comic) => (
                    <ComicCard key={comic.id} comic={comic} variant="horizontal" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6 text-center">
            为什么选择韩漫漫画网？
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {[
              { icon: 'ri-shield-check-line', title: '高清画质', desc: '所有漫画均采用高清分辨率，细节清晰可见，为您带来极致的视觉享受。' },
              { icon: 'ri-flashlight-line', title: '加载迅速', desc: '优化的服务器和CDN加速，确保每一页漫画都能秒速加载，告别等待。' },
              { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '专为移动端优化的无遮挡下拉式阅读体验，流畅自然，沉浸感十足。' },
              { icon: 'ri-shield-star-line', title: '完全免费', desc: '全站漫画永久免费开放，无需注册即可畅读，让好内容零门槛触达。' },
            ].map((feature, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-background-100/70">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-4">
                  <i className={`${feature.icon} text-2xl text-primary-500`}></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}